import { atom } from 'jotai';

import { chronicleDeviceStateAtom } from './connection';

/** Mirrors `MIN_CHUNK_LIVE_FW`; the cross-language pin in the test keeps them equal. */
export const MIN_CHUNK_LIVE_FW = [5, 0];

// Mirror of tauri-plugin-chronicle core::version::parse_fw_numeric_components:
// dotted decimals, trailing alphabetic CI tag on the last component only.
export const parseFwNumericComponents = (version: string): number[] | null => {
  const parts = version.trim().split('.');
  const last = parts.length - 1;
  const raw = parts.map((p, i) => (i === last ? p.replace(/[a-zA-Z]+$/, '') : p));
  return raw.every((p) => /^\d+$/.test(p)) ? raw.map(Number) : null;
};

export const isFwAtLeast = (components: number[], min: number[]): boolean => {
  for (let i = 0; i < Math.max(components.length, min.length); i++) {
    if ((components[i] ?? 0) !== (min[i] ?? 0))
      return (components[i] ?? 0) > (min[i] ?? 0);
  }
  return true;
};

/**
 * Whether the linked Remote runs chunk-live firmware (5.0+), projecting the
 * Rust verdict in `remote_chunk_sync::capability` from the same
 * `firmwareVersion` input.
 *
 * **Polarity is deliberately the opposite of Rust's.** There, an unresolved
 * version must never be read as legacy — picking the stream pipeline on a real
 * 5.0 device would delete the only copy of the audio. Here the risk runs the
 * other way: this gates *presentation* (mid-recording bulk-sync widget
 * suppression), and the failure to avoid is showing a 4.x clinician 5.0-only
 * chrome. So unresolved, unparseable and "no device yet" all resolve `false`
 * — pre-5.0 behaviour is the safe default, and nothing changes for a user
 * until a 5.0 device is actually seen.
 *
 * Survives a disconnect: `mapDeviceState` preserves `firmwareVersion` through
 * the `disconnected` branch. It does not survive an app restart with the
 * device still away — that falls back to the 4.x presentation until the next
 * connection, which is the conservative direction.
 */
export const chronicleSupportsChunkLiveAtom = atom((get) => {
  const firmwareVersion = get(chronicleDeviceStateAtom).firmwareVersion;
  if (firmwareVersion === null) return false;
  const components = parseFwNumericComponents(firmwareVersion);
  return components !== null && isFwAtLeast(components, MIN_CHUNK_LIVE_FW);
});

/**
 * Sessions whose chunk-live stop has begun and whose stop IPC has not
 * returned. A set rather than a flag: a second stop can start while an
 * earlier drain is still running, and a shared boolean would let the shorter
 * one clear the longer one's suppression.
 */
export const chronicleChunkLiveStoppingSessionsAtom = atom<ReadonlySet<string>>(
  new Set<string>()
);

/**
 * True while any chunk-live stop is still draining.
 * `chronicleIsRecordingAtom` drops at the start of the stop while the drain
 * keeps running, so presentation gated on "recording" must span both.
 */
export const chronicleChunkLiveStopInFlightAtom = atom(
  (get) => get(chronicleChunkLiveStoppingSessionsAtom).size > 0
);
